import { state } from "./workspaceStore.js";
import {
  clone,
  save,
  product,
  transitionOrder,
  id,
  calculateOrder,
  notices,
  validateCollection,
  adjustStock,
  createOrder,
  orderTotal,
  validateSettings,
} from "../utils/domain/model.js";
import { $, num } from "../utils/format.js";
import {
  getWorkspaceSession as getSession,
  closeWorkspace as endSession,
  loginWorkspace,
} from "../utils/domain/workspace.js";
import { assignCollectionProducts } from "../utils/domain/workspace.js";
import {
  sellingPrice,
  validateCoupon,
  periodBounds,
  customerRecord,
  validateCustomer,
} from "../utils/domain/operations.js";
import { nav } from "../config/navigation.js";
import { flushSync } from "react-dom";
import { seed } from "../data/catalogue.js";
import { makeExport, downloadExport } from "../utils/domain/csv.js";
import { resetTablePages } from "../components/tables/RecordTable.jsx";
import { customer } from "./selectors.js";
export function flash(text) {
  state.toast = text;
  clearTimeout(state.toastTimer);
  state.toastTimer = setTimeout(() => {
    state.toast = "";
    render();
  }, 5000);
}
export function persist(mutator, msg = "Changes saved", target = null) {
  if (state.loadError) {
    showError(state.loadError);
    return false;
  }
  let next = clone(state.db);
  try {
    let result = mutator(next);
    save(localStorage, next);
    state.db = next;
    state.storageError = "";
    flash(msg);
    if (msg !== "Product assignment saved") state.formBaseline = null;
    if (target) {
      state.navigationApproved = true;
      navigate(target.route, target.id);
    } else render();
    return result ?? true;
  } catch (e) {
    showError(
      e.name === "QuotaExceededError"
        ? "Could not save. Browser storage is full; reduce uploaded image sizes. No saved records were changed."
        : e.message || "Could not save changes.",
    );
    return false;
  }
}
export function showError(text) {
  let el = $("#dialog-error") || $("#form-error");
  if (el) {
    el.textContent = text;
    el.focus();
  } else {
    state.storageError = text;
    render();
  }
}
export function parseRoute() {
  let parts;
  try {
    parts = decodeURIComponent(location.hash.slice(1)).split("/");
  } catch {
    parts = ["dashboard"];
  }
  if (parts[0] === "inventory") {
    history.replaceState(null, "", "#products");
    parts = ["products"];
  }
  const r = parts[0] || "dashboard";
  let rid = parts[1] || null;
  if (r === "order" && state.db?.orders.some((o) => o.legacyId === rid))
    rid = state.db.orders.find((o) => o.legacyId === rid).id;
  return {
    route: r,
    id: rid,
  };
}
export function navigate(route, recordId = null) {
  location.hash = route + (recordId ? "/" + encodeURIComponent(recordId) : "");
  if (state.current.route === route && state.current.id === recordId) render();
  else routeChanged();
}
export function routeChanged() {
  let next = parseRoute();
  if (next.route === "signup") {
    next = {
      route: "login",
      id: null,
    };
    history.replaceState(null, "", "#login");
  }
  state.activeAccount = getSession(localStorage, sessionStorage);
  state.signedIn = !!state.activeAccount;
  if (!state.signedIn && !["login", "signup"].includes(next.route)) {
    state.returnRoute = next;
    next = {
      route: "login",
      id: null,
    };
    history.replaceState(null, "", "#login");
  } else if (state.signedIn && ["login", "signup"].includes(next.route)) {
    next = {
      route: "dashboard",
      id: null,
    };
    history.replaceState(null, "", "#dashboard");
  }
  if (next.route !== state.current.route || next.id !== state.current.id) {
    if (
      state.signedIn &&
      !state.navigationApproved &&
      isFormDirty() &&
      window.confirm &&
      !window.confirm("Discard unsaved changes?")
    ) {
      history.replaceState(
        null,
        "",
        "#" +
          state.current.route +
          (state.current.id ? "/" + encodeURIComponent(state.current.id) : ""),
      );
      return;
    }
    state.navigationApproved = false;
    state.formBaseline = null;
    for (const [key, paginationState] of state.tableStates)
      if (key.startsWith(next.route + "|")) paginationState.page = 1;
    state.query = "";
    state.filter = "All";
    state.draft = null;
    state.draftKey = "";
    state.orderDraft = null;
    state.quote = null;
    state.profileDraft = null;
    state.collectionDraft = null;
    state.couponDraft = null;
    state.couponSearch = "";
    state.lastProductOptions = [];
    state.dialog = null;
    state.authErrors = {};
  }
  state.current = next;
  render();
  window.scrollTo(0, 0);
}
export function captureProduct() {
  let el = $("#product-form");
  if (!el) return;
  let f = new FormData(el);
  state.draft.labelMatch = f.has("labelMatch");
  for (let k of [
    "name",
    "collection",
    "variant",
    "status",
    "description",
    "ingredients",
    "nutrition",
    "allergens",
    "shelfLife",
    "storage",
    "manufacturer",
    "marketer",
    "fssai",
    "infoMode",
  ])
    state.draft[k] = String(f.get(k) || "").trim();
  state.draft.taxRate =
    f.get("taxRate") === "" ? null : Number(f.get("taxRate"));
  state.draft.complianceVerified = f.has("complianceVerified");
  state.draft.stockReason = String(f.get("stockReason") || "");
  state.draft.skus.forEach((s, i) => {
    for (let k of ["code", "variant", "unit", "dimensions"])
      s[k] = String(f.get(`s${i}-${k}`) || "").trim();
    for (let k of [
      "qty",
      "mrp",
      "discount",
      "price",
      "stock",
      "threshold",
      "weight",
    ])
      s[k] = num(f.get(`s${i}-${k}`));
    const old = product(state.db, state.draft.id)?.skus.find(
      (x) => x.id === s.id,
    );
    if (!old || s.mrp !== old.mrp || s.discount !== old.discount)
      s.price = sellingPrice(s.mrp, s.discount);
  });
  return f;
}
export function captureOrder() {
  const f = new FormData($("#order-new-form"));
  state.orderDraft.customerId = String(f.get("customerId"));
  state.orderDraft.coupon = String(f.get("coupon") || "").trim();
  state.orderDraft.offer = String(f.get("offer") || "");
  state.orderDraft.lines.forEach((l, i) => {
    const [pid, sid] = String(f.get("line-" + i) || "").split("|");
    l.productId = pid;
    l.skuId = sid;
    l.qty = Number(f.get("qty-" + i));
  });
}
export function captureCoupon(f = new FormData($("#coupon-form"))) {
  const c = state.couponDraft.coupon,
    scope = String(f.get("applicability") || "all"),
    single = scope.startsWith("product:");
  Object.assign(c, {
    code: String(f.get("couponCode") || "")
      .trim()
      .toUpperCase(),
    percent: num(f.get("couponPercent")),
    minimum: num(f.get("couponMinimum")),
    active: f.has("couponActive"),
    newOnly: f.has("newOnly"),
    stacking: f.has("stacking"),
    applicability: scope === "all" ? "all" : "selected",
  });
  if (single) c.productIds = [scope.slice(8)];
  else if (c.applicability === "selected")
    c.productIds = [
      ...new Set([
        ...(c.productIds || []).filter(
          (pid) => !state.lastProductOptions.includes(pid),
        ),
        ...state.lastProductOptions.filter((pid) => f.has("product-" + pid)),
      ]),
    ];
  state.couponDraft.welcome = f.has("welcome15");
  return c;
}
export function saveCouponForm(f) {
  const c = clone(captureCoupon(f)),
    welcome = state.couponDraft.welcome;
  persist(
    (n) => {
      validateCoupon(n, c);
      if (
        welcome &&
        c.code !== "WELCOME15" &&
        (n.coupons.some((x) => x.id !== c.id && x.code === "WELCOME15") ||
          n.couponAliases.some(
            (a) => a.code === "WELCOME15" && a.couponId !== c.id,
          ))
      )
        throw Error("WELCOME15 already belongs to another coupon.");
      const index = n.coupons.findIndex((x) => x.id === c.id);
      if (index < 0) n.coupons.push(c);
      else n.coupons[index] = c;
      const hadAlias = n.couponAliases.some(
        (a) => a.couponId === c.id && a.code === "WELCOME15",
      );
      n.couponAliases = n.couponAliases.filter(
        (a) => a.couponId !== c.id || a.code !== "WELCOME15",
      );
      if (c.code !== "WELCOME15" && (welcome || hadAlias))
        n.couponAliases.push({
          code: "WELCOME15",
          couponId: c.id,
          active: welcome,
        });
    },
    "Coupon saved",
    {
      route: "coupons",
    },
  );
}
export function render() {
  document.title =
    "Praachin Organics · " +
    (state.signedIn
      ? nav.find((n) => n[0] === state.current.route)?.[2] || "Admin workspace"
      : "Login");
  flushSync(() => state.refresh());
  if (state.formBaseline === null) state.formBaseline = formSnapshot();
}
export function captureProfile() {
  const f = new FormData($("#profile-form"));
  for (const k of [
    "name",
    "designation",
    "company",
    "brand",
    "businessEmail",
    "phone",
  ])
    state.profileDraft[k] = String(f.get(k) || "").trim();
}
export function captureCollection() {
  const f = new FormData($("#collection-form"));
  for (const k of ["name", "code", "description"])
    state.collectionDraft[k] = String(f.get(k) || "").trim();
  state.collectionDraft.order = Number(f.get("order"));
  state.collectionDraft.launch = f.get("launch") === "active";
  state.collectionDraft.selectedProductIds = state.db.products
    .filter((p) => f.has("member-" + p.id))
    .map((p) => p.id);
  state.collectionDraft.destination = String(f.get("destination") || "");
}
export function captureBanner() {
  const f = new FormData($("#banner-form"));
  state.draft.title = String(f.get("title") || "").trim();
  state.draft.body = String(f.get("body") || "").trim();
  state.draft.active = f.has("active");
  state.draft.approved = f.has("approved");
}
export async function readImage(file, avatarOnly = false) {
  if (!file) return "";
  if (!["image/jpeg", "image/png", "image/webp"].includes(file.type))
    throw Error("Use a JPEG, PNG or WebP image.");
  if (file.size > 2 * 1024 * 1024)
    throw Error("Image must be no larger than 2 MB.");
  const src = await new Promise((r, j) => {
    const fr = new FileReader();
    fr.onload = () => r(fr.result);
    fr.onerror = () => j(Error("Could not read image."));
    fr.readAsDataURL(file);
  });
  const im = new Image();
  im.src = src;
  try {
    await im.decode();
  } catch {
    throw Error("Image content is invalid or unreadable.");
  }
  const limit = avatarOnly ? 256 : 1000,
    scale = Math.min(1, limit / Math.max(im.width, im.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(im.width * scale));
  canvas.height = Math.max(1, Math.round(im.height * scale));
  canvas.getContext("2d").drawImage(im, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL("image/webp", 0.85);
}
export function openDialog(data) {
  state.dialog = {
    ...data,
    returnFocus: document.activeElement,
  };
  render();
}
export function closeDialog() {
  const old = state.dialog;
  state.dialog = null;
  $("#action-dialog")?.close?.();
  render();
  old?.returnFocus?.focus();
}
export function confirmAction() {
  let x = state.dialog;
  if (!x) return;
  if (x.kind === "logout") {
    try {
      endSession(sessionStorage);
    } catch (err) {
      showError(err.message);
      return;
    }
    closeDialog();
    state.activeAccount = null;
    state.signedIn = false;
    state.draft = null;
    state.profileDraft = null;
    state.toast = "";
    state.returnRoute = null;
    state.authMessage = "You have signed out.";
    state.formBaseline = null;
    state.navigationApproved = true;
    navigate("login");
    return;
  }
  let ok = persist(
    (next) => {
      switch (x.kind) {
        case "delete-coupon":
          next.coupons = next.coupons.filter((c) => c.id !== x.id);
          next.couponAliases = next.couponAliases.filter(
            (a) => a.couponId !== x.id,
          );
          break;
        case "delete-promotion":
          next.promotions[x.id].active = false;
          next.promotions[x.id].deleted = true;
          break;
        case "delete-alias":
          next.couponAliases = next.couponAliases.filter(
            (a) => a.code !== x.id,
          );
          break;
        case "archive-product":
          product(next, x.id).archived = true;
          break;
        case "archive-collection": {
          if (next.products.some((p) => p.collection === x.id))
            throw Error(
              "Reassign all product members, including archived products, before archiving this collection.",
            );
          next.collections.find((c) => c.id === x.id).archived = true;
          break;
        }
        case "delete-banner":
          next.banners = next.banners.filter((b) => b.id !== x.id);
          break;
        case "cancel":
          transitionOrder(next, x.id, "Cancelled");
          break;
        case "reset": {
          const fresh = seed();
          Object.keys(next).forEach((k) => delete next[k]);
          Object.assign(next, fresh);
          break;
        }
      }
    },
    x.kind === "cancel" ? "Order cancelled" : "Changes saved",
    ["delete-coupon", "delete-promotion", "delete-alias"].includes(x.kind)
      ? {
          route: "coupons",
        }
      : x.kind === "delete-banner"
        ? {
            route: "settings",
          }
        : x.kind === "archive-collection"
          ? {
              route: "collections",
            }
          : x.kind === "reset"
            ? {
                route: "dashboard",
              }
            : null,
  );
  if (ok) {
    closeDialog();
    render();
  }
}
export async function handleClick(e) {
  const b = e.target.closest("button");
  if (!b || b.disabled) return;
  const d = b.dataset;
  if (d.togglePassword) {
    const input = document.getElementById(d.togglePassword),
      show = input.type === "password";
    input.type = show ? "text" : "password";
    b.textContent = show ? "Hide" : "Show";
    b.setAttribute("aria-pressed", String(show));
    b.setAttribute(
      "aria-label",
      (show ? "Hide" : "Show") +
        " " +
        (input.name === "confirmPassword" ? "confirm password" : "password"),
    );
    return;
  }
  if (d.pageKey) {
    const paginationState = state.tableStates.get(d.pageKey);
    if (paginationState) {
      preserveFormRender(() => (paginationState.page = Number(d.page)));
    }
    return;
  }
  if (d.nav) {
    if (b.textContent?.trim() === "Cancel") state.navigationApproved = true;
    navigate(d.nav, d.id || null);
    return;
  }
  if (
    !state.signedIn &&
    ["login", "signup"].includes(state.current.route) &&
    b.type === "submit"
  )
    return;
  if (!getSession(localStorage, sessionStorage)) {
    routeChanged();
    return;
  }
  if (d.export) {
    try {
      const file = makeExport(d.export, state.db, state.query, state.filter);
      downloadExport(file);
      flash(file.count + " matching records exported");
      render();
    } catch (err) {
      flash(err.message);
      render();
    }
    return;
  }
  if (d.stock) {
    openDialog({
      kind: "stock",
      id: d.stock,
    });
    return;
  }
  if (d.confirm) {
    openDialog({
      kind: d.confirm,
      id: d.id,
    });
    return;
  }
  if (d.read) {
    persist((n) => {
      n.notificationsRead = [...new Set([...n.notificationsRead, d.read])];
    }, "Notification marked read");
    return;
  }
  if (d.restoreProduct) {
    persist((n) => {
      product(n, d.restoreProduct).archived = false;
    }, "Product restored");
    return;
  }
  if (d.restoreCollection) {
    persist((n) => {
      n.collections.find((c) => c.id === d.restoreCollection).archived = false;
    }, "Collection restored");
    return;
  }
  if (d.removeSku !== undefined) {
    captureProduct();
    const i = Number(d.removeSku);
    state.draft.skus.splice(i, 1);
    render();
    return;
  }
  if (d.removePhoto !== undefined) {
    captureProduct();
    state.draft.photos.splice(Number(d.removePhoto), 1);
    if (!state.draft.photos.some((x) => x.startsWith("data:")))
      state.draft.uploadUnconfirmed = false;
    render();
    return;
  }
  if (d.removeLine !== undefined) {
    captureOrder();
    state.orderDraft.lines.splice(Number(d.removeLine), 1);
    state.quote = null;
    render();
    return;
  }
  try {
    switch (d.action) {
      case "select-products":
        captureCoupon();
        state.couponDraft.coupon.productIds = [
          ...new Set([
            ...state.couponDraft.coupon.productIds,
            ...state.db.products
              .filter((p) => !p.archived && p.status === "Active")
              .map((p) => p.id),
          ]),
        ];
        render();
        break;
      case "clear-products":
        captureCoupon();
        state.couponDraft.coupon.productIds = [];
        render();
        break;
      case "logout":
        openDialog({
          kind: "logout",
        });
        break;
      case "collapse-sidebar":
        persist(
          (n) => (n.sidebarCollapsed = !n.sidebarCollapsed),
          "Sidebar preference saved",
        );
        break;
      case "reset-period":
        state.periodDraft = null;
        persist(
          (n) =>
            (n.dashboardPeriod = {
              mode: "all",
            }),
          "All time selected",
        );
        break;
      case "menu": {
        const open = $(".sidebar")?.classList.toggle("mobile-open");
        $(".menu-toggle")?.setAttribute("aria-expanded", String(!!open));
        if (open) $(".drawer-close")?.focus();
        else $(".menu-toggle")?.focus();
        break;
      }
      case "replace-product-image":
        captureProduct();
        state.draft.replaceMain = true;
        $("#product-image").click();
        break;
      case "save-assignment":
        saveProductAssignment();
        break;
      case "remove-avatar":
        captureProfile();
        state.profileDraft.avatar = "";
        render();
        break;
      case "remove-banner-image":
        captureBanner();
        state.draft.image = "";
        render();
        break;
      case "add-line":
        captureOrder();
        state.orderDraft.lines.push({
          productId: "",
          skuId: "",
          qty: 1,
        });
        state.quote = null;
        render();
        break;
      case "calculate-order":
        captureOrder();
        state.quote = calculateOrder(
          state.db,
          state.orderDraft.lines,
          state.orderDraft.customerId,
          state.orderDraft.coupon,
          state.orderDraft.offer,
        );
        render();
        break;
      case "read-all":
        persist((n) => {
          n.notificationsRead = [
            ...new Set([
              ...n.notificationsRead,
              ...notices(n).map((x) => x.signature),
            ]),
          ];
        }, "Notifications marked read");
        break;
      case "export-data": {
        const blob = new Blob([JSON.stringify(state.db, null, 2)], {
            type: "application/json",
          }),
          url = URL.createObjectURL(blob),
          a = document.createElement("a");
        a.href = url;
        a.download = "praachin-admin-backup.json";
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        break;
      }
      case "close-dialog":
        closeDialog();
        break;
      case "confirm-dialog":
        confirmAction();
        break;
    }
  } catch (err) {
    showError(err.message);
  }
}
export function handleInput(e) {
  if (e.target.id === "coupon-search") {
    const caret = e.target.selectionStart;
    captureCoupon();
    state.couponSearch = e.target.value;
    render();
    $("#coupon-search")?.focus();
    $("#coupon-search")?.setSelectionRange(caret, caret);
    return;
  }
  const match = e.target.name?.match(/^s(\d+)-(mrp|discount)$/);
  if (match) {
    const i = match[1],
      mrp = $(`[name="s${i}-mrp"]`)?.value,
      discount = $(`[name="s${i}-discount"]`)?.value,
      out = $(`[name="s${i}-price"]`);
    const value = sellingPrice(mrp, discount);
    if (out) out.value = value == null ? "" : value.toFixed(2);
    return;
  }
  if (e.target.id === "search") {
    let caret = e.target.selectionStart;
    state.query = e.target.value;
    resetTablePages();
    render();
    $("#search").focus();
    $("#search").setSelectionRange(caret, caret);
  }
}
export async function handleChange(e) {
  const el = e.target;
  try {
    if (el.name === "applicability") {
      captureCoupon();
      render();
      return;
    }
    if (el.name === "mode" && el.closest("#period-form")) {
      const f = new FormData($("#period-form"));
      state.periodDraft = {
        mode: el.value,
        day: f.get("day"),
        month: f.get("month"),
        year: f.get("year"),
        start: f.get("start"),
        end: f.get("end"),
      };
      render();
      return;
    }
    if (el.dataset?.pageSize) {
      const paginationState = state.tableStates.get(el.dataset.pageSize);
      if (paginationState)
        preserveFormRender(() => {
          paginationState.size = Number(el.value);
          paginationState.page = 1;
        });
      return;
    }
    if (el.id === "filter") {
      state.filter = el.value;
      resetTablePages();
      render();
      return;
    }
    if (el.id === "avatar-image" && el.files[0]) {
      captureProfile();
      state.profileDraft.avatar = await readImage(el.files[0], true);
      render();
      return;
    }
    if (el.id === "banner-image" && el.files[0]) {
      captureBanner();
      state.draft.image = await readImage(el.files[0]);
      render();
      return;
    }
    if (el.id === "product-image" && el.files[0]) {
      captureProduct();
      const src = await readImage(el.files[0]);
      if (state.draft.replaceMain && state.draft.photos.length)
        state.draft.photos[0] = src;
      else state.draft.photos.unshift(src);
      state.draft.replaceMain = false;
      state.draft.uploadUnconfirmed = true;
      state.draft.labelMatch = false;
      state.draft.imageFeedback =
        "Image ready. Confirm the packaging label, then Save to persist it.";
      render();
      return;
    }
    if (e.target.closest("#order-new-form")) state.quote = null;
  } catch (err) {
    const slot = el.id === "product-image" ? $("#image-error") : null;
    if (slot) slot.textContent = err.message;
    else showError(err.message);
    el.value = "";
  }
}
export async function handleSubmit(e) {
  e.preventDefault();
  if (e.target.id === "login-form") {
    await submitLogin(e.target);
    return;
  }
  if (!getSession(localStorage, sessionStorage)) {
    routeChanged();
    return;
  }
  const f = new FormData(e.target);
  try {
    switch (e.target.id) {
      case "period-form": {
        const p = Object.fromEntries(
          ["mode", "day", "month", "year", "start", "end"].map((k) => [
            k,
            String(f.get(k) || ""),
          ]),
        );
        periodBounds(p);
        state.periodDraft = null;
        persist((n) => (n.dashboardPeriod = p), "Dashboard period applied");
        break;
      }
      case "coupon-form": {
        saveCouponForm(f);
        break;
      }
      case "collection-form": {
        captureCollection();
        const c = clone(state.collectionDraft),
          members = c.selectedProductIds,
          destination = c.destination;
        delete c.selectedProductIds;
        delete c.destination;
        delete c.assignmentMessage;
        persist(
          (n) => {
            validateCollection(n, c);
            const idx = n.collections.findIndex((x) => x.id === c.id);
            if (idx < 0) n.collections.push(c);
            else n.collections[idx] = c;
            assignCollectionProducts(n, c.id, members, destination);
          },
          "Collection saved",
          {
            route: "collection",
            id: c.id,
          },
        );
        break;
      }
      case "stock-form": {
        const [pid, sid] = state.dialog.id.split("|");
        let ok = persist(
          (n) =>
            adjustStock(
              n,
              pid,
              sid,
              Number(f.get("stock")),
              Number(f.get("threshold")),
              String(f.get("reason") || ""),
            ),
          "Stock adjustment saved",
        );
        if (ok) {
          closeDialog();
          render();
        }
        break;
      }
      case "order-status-form": {
        const next = String(f.get("status")),
          oid = state.current.id;
        if (next === "Cancelled") {
          openDialog({
            kind: "cancel",
            id: oid,
          });
          return;
        }
        persist(
          (n) =>
            transitionOrder(
              n,
              oid,
              next,
              String(f.get("carrier") || ""),
              String(f.get("tracking") || ""),
            ),
          "Order status updated",
        );
        break;
      }
      case "order-new-form": {
        captureOrder();
        let created;
        persist((n) => {
          created = createOrder(
            n,
            state.orderDraft.lines,
            state.orderDraft.customerId,
            state.orderDraft.coupon,
            state.orderDraft.offer,
          );
          return created;
        }, "Order created");
        if (created && state.db.orders.some((o) => o.id === created.id))
          navigate("order", created.id);
        break;
      }
      case "payment-form": {
        const pid = state.current.id,
          status = String(f.get("status"));
        persist((n) => {
          const p = n.payments.find((p) => p.id === pid),
            allowed = {
              Pending: ["Pending", "Success", "Failed"],
              Success: ["Success", "Refunded"],
              Failed: ["Failed", "Pending", "Success"],
              Refunded: ["Refunded"],
            };
          if (!allowed[p.status]?.includes(status))
            throw Error("Invalid payment record transition.");
          const refund = num(f.get("refundedAmount")) ?? 0,
            linked = n.orders.find((o) => o.id === p.orderId),
            total = linked ? orderTotal(linked) : p.amount || 0;
          if (!Number.isFinite(refund) || refund < 0 || refund > total)
            throw Error("Refund must be between zero and the payment amount.");
          if (refund > 0 && !["Success", "Refunded"].includes(status))
            throw Error("Refunds require a successful payment.");
          p.refundedAmount = status === "Refunded" ? total : refund;
          p.status = status;
          p.reference = String(f.get("reference") || "").trim();
        }, "Transaction record saved");
        break;
      }
      case "customer-edit-form": {
        const old = customer(state.current.id),
          r = customerRecord(state.db, old),
          c = clone(old);
        for (const k of ["name", "email", "phone", "city", "note"])
          c[k] = String(f.get(k) || "").trim();
        const address = String(f.get("address") || "").trim();
        if (address !== r.address) c.address = address;
        c.status = String(f.get("status") || "");
        const date = String(f.get("signupDate") || "");
        if (date !== String(r.registration).slice(0, 10)) c.signupDate = date;
        validateCustomer(state.db, c);
        persist(
          (n) => {
            n.customers[n.customers.findIndex((x) => x.id === c.id)] = c;
          },
          "Customer saved",
          {
            route: "customer",
            id: c.id,
          },
        );
        break;
      }
      case "customer-form":
        persist(
          (n) =>
            (n.customers.find((c) => c.id === state.current.id).note = String(
              f.get("note") || "",
            ).trim()),
          "Support note saved",
        );
        break;
      case "profile-form": {
        captureProfile();
        const p = clone(state.profileDraft);
        if (["name", "designation", "company", "brand"].some((k) => !p[k]))
          throw Error("Complete the required identity fields.");
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(p.businessEmail))
          throw Error("Enter a valid business email.");
        if (!/^\+?[0-9 ()-]{7,18}$/.test(p.phone))
          throw Error("Enter a valid business contact number.");
        persist((n) => {
          n.profile = p;
        }, "Profile saved");
        break;
      }
      case "banner-form": {
        captureBanner();
        const b = clone(state.draft);
        if (!b.title) throw Error("A banner title is required.");
        persist((n) => {
          n.banners[n.banners.findIndex((x) => x.id === b.id)] = b;
        }, "Banner saved");
        break;
      }
      case "settings-form": {
        persist((n) => {
          const s = n.settings;
          for (const k of [
            "legalEntity",
            "brand",
            "businessEmail",
            "businessPhone",
            "supportEmail",
            "supportPhone",
            "address",
            "currency",
            "coverage",
            "dispatchEstimate",
          ])
            s[k] = String(f.get(k) || "").trim();
          for (const k of ["minimumOrder", "shippingFee", "freeThreshold"])
            s[k] = Number(f.get(k));
          for (const k of [
            "gstInclusive",
            "cod",
            "partialShipments",
            "weekendProcessing",
          ])
            s[k] = f.has(k);
          validateSettings(s);
        }, "Settings saved");
        break;
      }
    }
  } catch (err) {
    showError(err.message);
  }
}
export function handleKeyDown(e) {
  if (e.key === "Escape") {
    if (state.dialog) {
      e.preventDefault();
      closeDialog();
      return;
    }
    const sidebar = $(".sidebar");
    sidebar?.classList.remove("mobile-open");
    $(".menu-toggle")?.setAttribute("aria-expanded", "false");
    $(".menu-toggle")?.focus();
  }
}
export function initialize() {
  window.addEventListener("hashchange", routeChanged);
  window.addEventListener("pageshow", routeChanged);
  routeChanged();
  return () => {
    window.removeEventListener("hashchange", routeChanged);
    window.removeEventListener("pageshow", routeChanged);
  };
}
export function saveProductAssignment() {
  captureCollection();
  const c = clone(state.collectionDraft),
    members = c.selectedProductIds,
    destination = c.destination;
  delete c.selectedProductIds;
  delete c.destination;
  delete c.assignmentMessage;
  const existed = state.db.collections.some((x) => x.id === c.id);
  const ok = persist((n) => {
    if (!existed) {
      validateCollection(n, c);
      n.collections.push(c);
    }
    assignCollectionProducts(n, c.id, members, destination);
  }, "Product assignment saved");
  if (ok) {
    state.collectionDraft.selectedProductIds = state.db.products
      .filter((p) => !p.archived && p.collection === c.id)
      .map((p) => p.id);
    state.collectionDraft.assignmentMessage =
      "Product assignment saved. Collection counts and product records are updated.";
    if (!existed) {
      state.current.id = c.id;
      history.replaceState(
        null,
        "",
        "#collection-edit/" + encodeURIComponent(c.id),
      );
    }
    render();
  }
}
export async function submitLogin(form) {
  if (state.authBusy) return;
  const f = new FormData(form),
    button = form.querySelector('[type="submit"]'),
    slot = $("#access-error");
  slot.textContent = "";
  const username = String(f.get("username") || "").trim(),
    password = String(f.get("password") || "");
  for (const name of ["username", "password"])
    form.elements?.namedItem(name)?.removeAttribute("aria-invalid");
  if (!username || !password) {
    slot.textContent = "Enter your username and password.";
    for (const name of ["username", "password"])
      if (!String(f.get(name) || "").trim())
        form.elements?.namedItem(name)?.setAttribute("aria-invalid", "true");
    slot.focus();
    return;
  }
  state.authBusy = true;
  button.disabled = true;
  button.textContent = "Logging in…";
  button.setAttribute("aria-busy", "true");
  try {
    state.activeAccount = await loginWorkspace(localStorage, sessionStorage, {
      username,
      password,
    });
    state.signedIn = true;
    state.authMessage = "";
    navigate("dashboard");
  } catch (error) {
    slot.textContent = error.message;
    slot.focus();
  } finally {
    state.authBusy = false;
    button.disabled = false;
    button.textContent = "Login";
    button.removeAttribute("aria-busy");
  }
}
export function editableForm() {
  const ids = {
    "product-edit": "product-form",
    "collection-edit": "collection-form",
    "coupon-edit": "coupon-form",
    "customer-edit": "customer-edit-form",
    settings: "settings-form",
    profile: "profile-form",
    customer: "customer-form",
    payment: "payment-form",
    order: "order-status-form",
    "order-new": "order-new-form",
  };
  return ids[state.current.route] ? $("#" + ids[state.current.route]) : null;
}
export function formValues() {
  const form = editableForm();
  if (!form) return null;
  const data = new FormData(form);
  return [...(data.entries ? data.entries() : data.map?.entries?.() || [])].map(
    ([key, value]) => [
      key,
      typeof value === "object" ? value.name + ":" + value.size : String(value),
    ],
  );
}
export function formSnapshot() {
  return JSON.stringify([
    formValues(),
    state.current.route === "product-edit" ? state.draft?.photos : null,
  ]);
}
export function isFormDirty() {
  return state.formBaseline !== null && state.formBaseline !== formSnapshot();
}
export function preserveFormRender(change) {
  const values = formValues();
  change();
  render();
  const form = editableForm();
  if (form?.elements && values) {
    const data = new Map(values);
    for (const el of form.elements) {
      if (!el.name || el.type === "file") continue;
      if (el.type === "checkbox") el.checked = data.has(el.name);
      else if (data.has(el.name)) el.value = data.get(el.name);
    }
  }
}

// Shared React primitives. Attribute metadata comes only from internal action helpers.
